/**
 * Community Membership API functions
 * Authenticated requests require passing the axios instance from useAuthenticatedAxios
 */

import publicAxios from './publicAxios';

/**
 * Get memberships with optional filters (public)
 * @param {object} filters - Optional filters { communityId, userId, status }
 */
export const getMemberships = async (filters = {}, axiosInstance = null) => {
    const params = new URLSearchParams();
    if (filters.communityId) params.append('communityId', filters.communityId);
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.status) params.append('status', filters.status);
    if (filters.limit !== undefined) params.append('limit', filters.limit);
    if (filters.offset !== undefined) params.append('offset', filters.offset);
    const client = axiosInstance || publicAxios;
    const response = await client.get('/api/memberships', { params });
    return response.data;
};

/**
 * Check if current user is a member of a community (authenticated)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} communityId - Community ID
 */
export const checkMembership = async (axiosInstance, communityId) => {
    const response = await axiosInstance.get(
        `/api/memberships/check/${encodeURIComponent(communityId)}`
    );
    return response.data;
};

/**
 * Join a community (authenticated)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} communityId - Community ID
 */
export const joinCommunity = async (axiosInstance, communityId) => {
    const response = await axiosInstance.post('/api/memberships/join', {
        communityId,
    });
    return response.data;
};

/**
 * Leave a community (authenticated)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} communityId - Community ID
 */
export const leaveCommunity = async (axiosInstance, communityId) => {
    const response = await axiosInstance.delete(
        `/api/memberships/leave/${communityId}`
    );
    return response.data;
};

/**
 * Update a member's role (authenticated, admin only)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} membershipId - Membership ID
 * @param {string} role - New role (member, moderator, admin)
 */
export const updateMemberRole = async (axiosInstance, membershipId, role) => {
    const response = await axiosInstance.put(
        `/api/memberships/${membershipId}/role`,
        { role }
    );
    return response.data;
};

/**
 * Ban a member from a community (authenticated, admin only)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} membershipId - Membership ID
 */
export const banMember = async (axiosInstance, membershipId, reason) => {
    const response = await axiosInstance.put(
        `/api/memberships/${membershipId}/ban`,
        { reason }
    );
    return response.data;
};

/**
 * Unban a member from a community (authenticated, admin only)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} membershipId - Membership ID
 */
export const unbanMember = async (axiosInstance, membershipId) => {
    const response = await axiosInstance.put(
        `/api/memberships/${membershipId}/unban`
    );
    return response.data;
};

/**
 * Submit a moderator application (authenticated).
 * NOTE: the server route keys off the membership _id, not the community id.
 * The membership _id comes back from checkMembership, so no extra lookup.
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} membershipId - Membership _id
 * @param {string} applicationText - Why the user wants to moderate
 */
export const submitModeratorApplication = async (
    axiosInstance,
    membershipId,
    applicationText
) => {
    const response = await axiosInstance.post(
        `/api/memberships/${encodeURIComponent(membershipId)}/apply`,
        { applicationText }
    );
    return response.data;
};

/**
 * Get a community's moderator applications (authenticated, admin or
 * community moderator)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {object} filters - Filters { communityId, status }. communityId is
 *   required by the server; status optionally narrows to pending/accepted/etc.
 */
export const getApplications = async (axiosInstance, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.communityId) params.append('communityId', filters.communityId);
    if (filters.status) params.append('status', filters.status);
    const response = await axiosInstance.get('/api/memberships/applications', {
        params,
    });
    return response.data;
};

/**
 * Decide (accept, reject, or dismiss) a moderator application (authenticated,
 * admin or community moderator).
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} membershipId - Membership ID that owns the application
 * @param {string} applicationId - Application subdocument ID
 * @param {object} body - { decision, decisionReason }. decision is one of
 *   'accepted' | 'rejected' | 'dismissed'; decisionReason is optional.
 */
export const decideApplication = async (
    axiosInstance,
    membershipId,
    applicationId,
    { decision, decisionReason = '' }
) => {
    const response = await axiosInstance.put(
        `/api/memberships/${membershipId}/applications/${applicationId}/decide`,
        { decision, decisionReason }
    );
    return response.data;
};
